import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as spine from '@esotericsoftware/spine-webgl'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'
import CanvasCore from 'CanvasCore'
import { useUI } from 'Editor'
import { useConfigStore } from 'AdminPanel/Spine/store/configStore'
import { useEditorStore as useSpineEditorStore } from 'AdminPanel/Spine/store/editorStore'
import { SpinePreviewApp } from 'AdminPanel/Spine/lib/SpinePreviewApp'
import { isStaticSpineTemplateType } from 'AdminPanel/Spine/types/spine-template'
import {
  zIndexForAvatarTemplateBg,
  zIndexForAvatarTemplateCharacterKonva,
  zIndexForSpineCanvas,
  zIndexForSpineCanvasAboveAvatar,
} from 'AdminPanel/Spine/lib/staticLayerStack'
import {
  buildAvatarMergedStaticStackItems,
  splitRelatedTemplatesByCharacterLayer,
} from 'Editor/lib/avatarSpinePreview'
import { getComponentCanvasSize } from 'Editor/utils/fullscreenType'

const CHECKER_STYLE = {
  backgroundImage: `
    linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
    linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
  `,
  backgroundSize: '20px 20px',
  backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
  backgroundColor: '#f5f5f5',
}

function syncCanvasPixelSize(canvasEl, hostEl) {
  if (!canvasEl || !hostEl) return
  const rect = hostEl.getBoundingClientRect()
  const size = Math.max(1, Math.floor(Math.min(rect.width, rect.height)))
  if (canvasEl.width !== size || canvasEl.height !== size) {
    canvasEl.width = size
    canvasEl.height = size
  }
}

/** 与 Spine 编辑器预览同构：统一 z-index 叠层 + WebGL */
const AvatarEditorPreview = ({ template, drawRef, charDrawRef }) => {
  const { tid } = useParams()
  const previewHostRef = useRef(null)
  const spineBelowCanvasRef = useRef(null)
  const spineAboveCanvasRef = useRef(null)
  const spineBelowSlotRef = useRef({ app: null, canvas: null })
  const spineAboveSlotRef = useRef({ app: null, canvas: null })
  const [error, setError] = useState(null)

  const setAvatarSpineExport = useUI((s) => s.setAvatarSpineExport)

  const { data: layers } = useSWR(['/layers', { tid }])

  const loadedRelatedTemplates = useConfigStore((s) => s.loadedRelatedTemplates)
  const allRelatedTemplateIds = useConfigStore((s) => s.allRelatedTemplateIds)
  const selectedSkins = useSpineEditorStore((s) => s.selectedSkins)

  const relatedTemplateOrderIndexById = useMemo(() => {
    const m = new Map()
    ;(allRelatedTemplateIds || []).forEach((id, i) => m.set(id, i))
    return m
  }, [allRelatedTemplateIds])

  const sortRelated = useCallback(
    (list) =>
      [...list].sort(
        (a, b) =>
          (relatedTemplateOrderIndexById.get(a.id) ?? 0) -
          (relatedTemplateOrderIndexById.get(b.id) ?? 0),
      ),
    [relatedTemplateOrderIndexById],
  )

  const relatedStaticOnly = useMemo(() => {
    const filtered = (loadedRelatedTemplates || []).filter(
      (rel) =>
        !rel.spineData && rel.config && isStaticSpineTemplateType(rel.type),
    )
    return sortRelated(filtered)
  }, [loadedRelatedTemplates, sortRelated])

  const staticStackItems = useMemo(
    () =>
      buildAvatarMergedStaticStackItems({
        relatedStatic: relatedStaticOnly.map((r) => ({
          id: r.id,
          type: r.type,
          config: r.config,
        })),
        selectedSkins,
        relatedTemplateOrderIndexById,
      }),
    [relatedStaticOnly, selectedSkins, relatedTemplateOrderIndexById],
  )

  const relatedSpineTemplates = useMemo(
    () => sortRelated((loadedRelatedTemplates || []).filter((r) => r.spineData)),
    [loadedRelatedTemplates, sortRelated],
  )

  const { belowCharacter: spineBelowTemplates, aboveCharacter: spineAboveTemplates } =
    useMemo(
      () => splitRelatedTemplatesByCharacterLayer(relatedSpineTemplates),
      [relatedSpineTemplates],
    )

  // 有任一关联 Spine 模版时，同时挂载 below/above 两个画布（空侧渲染为空），
  // 以保证导出视频时 below/above 的 app+canvas 引用稳定可用
  const hasSpine = relatedSpineTemplates.length > 0
  const needsSpineBelow = hasSpine
  const needsSpineAbove = hasSpine

  const previewStackItems = useMemo(() => {
    const items = [
      {
        kind: 'konva-bg',
        key: 'avatar-bg',
        zIndex: zIndexForAvatarTemplateBg(),
      },
      ...staticStackItems,
    ]
    if (needsSpineBelow) {
      items.push({
        kind: 'canvas',
        key: 'spine-webgl-below',
        zIndex: zIndexForSpineCanvas(),
      })
    }
    items.push({
      kind: 'konva-char',
      key: 'avatar-char',
      zIndex: zIndexForAvatarTemplateCharacterKonva(),
    })
    if (needsSpineAbove) {
      items.push({
        kind: 'canvas',
        key: 'spine-webgl-above',
        zIndex: zIndexForSpineCanvasAboveAvatar(),
      })
    }
    return items.sort((a, b) => a.zIndex - b.zIndex)
  }, [staticStackItems, needsSpineBelow, needsSpineAbove])

  const bgDrawerFilter = useMemo(() => {
    return (drawer) => {
      if (drawer.lid === 'draft') return false
      const layer = layers?.find((l) => l.id === drawer.lid)
      return layer?.background === 1
    }
  }, [layers])

  const charDrawerFilter = useMemo(() => {
    return (drawer) => {
      if (drawer.lid === 'draft') return true
      const layer = layers?.find((l) => l.id === drawer.lid)
      return layer?.background !== 1
    }
  }, [layers])

  useLayoutEffect(() => {
    if (!needsSpineBelow && !needsSpineAbove) {
      spineBelowSlotRef.current.canvas?.dispose()
      spineAboveSlotRef.current.canvas?.dispose()
      spineBelowSlotRef.current = { app: null, canvas: null }
      spineAboveSlotRef.current = { app: null, canvas: null }
      return undefined
    }

    let disposed = false
    let ro = null
    let rafId = 0

    const mountSlot = (canvasEl, host, templates, slotRef) => {
      slotRef.current.canvas?.dispose()
      if (!canvasEl) {
        slotRef.current = { app: null, canvas: null }
        return
      }
      const app = new SpinePreviewApp({
        spineData: null,
        relatedTemplates: templates,
        relatedTemplateIdsOrder: allRelatedTemplateIds || [],
        setError: (msg) => !disposed && setError(msg),
      })
      syncCanvasPixelSize(canvasEl, host)
      const spineCanvas = new spine.SpineCanvas(canvasEl, {
        app,
        webglConfig: {
          alpha: true,
          preserveDrawingBuffer: true,
          premultipliedAlpha: false,
          antialias: false,
        },
      })
      slotRef.current = { app, canvas: spineCanvas }
    }

    const cleanup = () => {
      disposed = true
      if (rafId) cancelAnimationFrame(rafId)
      ro?.disconnect()
      spineBelowSlotRef.current.canvas?.dispose()
      spineAboveSlotRef.current.canvas?.dispose()
      spineBelowSlotRef.current = { app: null, canvas: null }
      spineAboveSlotRef.current = { app: null, canvas: null }
    }

    const tryMount = () => {
      if (disposed) return
      const host = previewHostRef.current
      const belowEl = spineBelowCanvasRef.current
      const aboveEl = spineAboveCanvasRef.current
      if (!host || (needsSpineBelow && !belowEl) || (needsSpineAbove && !aboveEl)) {
        rafId = requestAnimationFrame(tryMount)
        return
      }

      setError(null)
      try {
        mountSlot(belowEl, host, spineBelowTemplates, spineBelowSlotRef)
        mountSlot(aboveEl, host, spineAboveTemplates, spineAboveSlotRef)
      } catch (err) {
        console.error('创建 SpineCanvas 失败:', err)
        setError(err instanceof Error ? err.message : '创建 SpineCanvas 失败')
        return
      }

      ro = new ResizeObserver(() => {
        if (disposed) return
        if (spineBelowCanvasRef.current) {
          syncCanvasPixelSize(spineBelowCanvasRef.current, host)
          spineBelowSlotRef.current.canvas?.renderer?.resize(1)
        }
        if (spineAboveCanvasRef.current) {
          syncCanvasPixelSize(spineAboveCanvasRef.current, host)
          spineAboveSlotRef.current.canvas?.renderer?.resize(1)
        }
      })
      ro.observe(host)
    }

    tryMount()
    return cleanup
  }, [
    needsSpineBelow,
    needsSpineAbove,
    spineBelowTemplates,
    spineAboveTemplates,
    allRelatedTemplateIds,
  ])

  // 发布导出视频所需引用：below/above Spine 槽 + 关联静态图层（Konva 层由 Overlay 从 drawRef/charDrawRef 栅格化）
  useEffect(() => {
    setAvatarSpineExport({
      belowSlotRef: spineBelowSlotRef,
      aboveSlotRef: spineAboveSlotRef,
      staticImgItems: staticStackItems,
    })
    return () => setAvatarSpineExport(null)
  }, [setAvatarSpineExport, staticStackItems])

  return (
    <div className="w-full h-full min-h-0 flex items-center justify-center overflow-hidden p-1">
      <div
        ref={previewHostRef}
        className="relative isolate aspect-square h-full w-auto max-w-full shrink-0 overflow-hidden rounded-md border border-border"
        style={CHECKER_STYLE}
      >
        {previewStackItems.map((item) => {
          if (item.kind === 'konva-bg') {
            return (
              <div
                key={item.key}
                className="absolute inset-0"
                style={{ zIndex: item.zIndex }}
              >
                <CanvasCore
                  ow={template.ow}
                  oh={template.oh}
                  baseSize={getComponentCanvasSize(template.fullscreenType).w}
                  baseHeight={getComponentCanvasSize(template.fullscreenType).h}
                  drawRef={drawRef}
                  drawerFilter={bgDrawerFilter}
                  showCheckerBackground={false}
                />
              </div>
            )
          }
          if (item.kind === 'konva-char') {
            return (
              <div
                key={item.key}
                className="absolute inset-0"
                style={{ zIndex: item.zIndex }}
              >
                <CanvasCore
                  ow={template.ow}
                  oh={template.oh}
                  baseSize={getComponentCanvasSize(template.fullscreenType).w}
                  baseHeight={getComponentCanvasSize(template.fullscreenType).h}
                  drawRef={charDrawRef ?? drawRef}
                  drawerFilter={charDrawerFilter}
                  showCheckerBackground={false}
                />
              </div>
            )
          }
          if (item.kind === 'img') {
            return (
              <img
                key={item.key}
                src={item.url}
                alt=""
                className="absolute inset-0 h-full w-full object-contain pointer-events-none select-none"
                style={{ zIndex: item.zIndex }}
                draggable={false}
              />
            )
          }
          if (item.kind === 'canvas') {
            const isAbove = item.key === 'spine-webgl-above'
            return (
              <canvas
                key={item.key}
                ref={isAbove ? spineAboveCanvasRef : spineBelowCanvasRef}
                className="absolute inset-0 h-full w-full rounded-md bg-transparent pointer-events-none"
                style={{ zIndex: item.zIndex }}
              />
            )
          }
          return null
        })}

        {error ? (
          <div className="absolute bottom-2 left-2 right-2 z-[60] rounded bg-background/90 p-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default AvatarEditorPreview
